import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LayoutProps, TypographyProps } from '@chakra-ui/react';
import { Button } from '@chakra-ui/react';
import { useSelect } from 'downshift';

import { CustomIcon } from '../icon';
import type { BaseItem, BaseProps } from './BaseSelect';
import BaseSelect from './BaseSelect';

type Props<Item extends BaseItem> = WithTestId<
  BaseProps<Item> &
    LayoutProps & {
      placeholder?: string;
      buttonLabelStyle?: TypographyProps;
    }
>;

const Select = <Item extends BaseItem>({
  isDisabled,
  isPending,
  size,
  items,
  onSelectedItemChange,
  width,
  selectedItem,
  idProperty = 'id',
  labelProperty = 'label',
  noItemsMessage,
  initialHighlightedIndex,
  isInvalid,
  placeholder,
  buttonLabelStyle,
  highlightedItemBg,
  subLabelColor,
  renderItemIcon,
  accentBorderColor,
  accentBoxShadow,
  'data-testid': testId = 'select',
}: Props<Item>) => {
  const { t } = useTranslation('shared');
  const [inputItems, setInputItems] = useState(items);

  useEffect(() => {
    setInputItems(items);
  }, [items]);

  const { isOpen, getMenuProps, getToggleButtonProps, highlightedIndex, getItemProps, inputValue } =
    useSelect<Item>({
      items: inputItems,
      selectedItem,
      initialHighlightedIndex,
      itemToString: (item) => item?.[labelProperty] as string,
      onSelectedItemChange({ selectedItem }) {
        onSelectedItemChange(selectedItem as Item | null);
      },
    });

  return (
    <BaseSelect<Item>
      items={inputItems}
      getItemProps={getItemProps}
      getMenuProps={getMenuProps}
      highlightedIndex={highlightedIndex}
      inputValue={inputValue}
      isOpen={isOpen}
      selectedItem={selectedItem}
      size={size}
      idProperty={idProperty}
      labelProperty={labelProperty}
      onSelectedItemChange={onSelectedItemChange}
      noItemsMessage={noItemsMessage}
      isInvalid={isInvalid}
      highlightedItemBg={highlightedItemBg}
      subLabelColor={subLabelColor}
      renderItemIcon={renderItemIcon}
      accentBorderColor={accentBorderColor}
      accentBoxShadow={accentBoxShadow}>
      <Button
        {...getToggleButtonProps()}
        borderColor={(isInvalid && 'secondary400') || undefined}
        size={size}
        isLoading={isPending}
        isDisabled={isDisabled || isPending}
        justifyContent={isPending ? 'center' : 'space-between'}
        variant={isOpen ? 'selectButtonOpen' : 'selectButton'}
        width={width}
        rightIcon={
          <CustomIcon size={20} name='chevron-down' color={isOpen ? 'primary500' : 'neutral400'} />
        }
        {...buttonLabelStyle}
        data-testid={testId}>
        {String(
          selectedItem ? selectedItem[labelProperty] : (placeholder ?? t('select.selectItem')),
        )}
      </Button>
    </BaseSelect>
  );
};

export default Select;
